import type {
  FederalPovertyLevel,
  HouseholdIncome,
  Prospect,
} from '@hc-frontend/core-types';
import { LeadStatus, QualifyingLifeEvent } from '@hc-frontend/core-types';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import type { Nullable } from '@hc-frontend/core-utils-types';
import {
  GetExternalLeadResponseStatus,
  getExternalLeadService,
  QualifyingLifeEventDto,
} from '@hc-frontend/services';

import { type GetProspectRepository } from '../../domain';
import { contactMapper } from './contact-mapper';
import { journeyAttributesMapper } from './journey-attributes-mapper';
import { locationMapper } from './location-mapper';
import { memberMapper } from './member-mapper';
import { userAttributesMapper } from './user-attributes-mapper';

const leadStatusMap = {
  [LeadStatus.Enum.FRESH2]: GetExternalLeadResponseStatus.FRESH2,
  [LeadStatus.Enum.PURCHASE_COMPLETED]:
    GetExternalLeadResponseStatus.PURCHASE_COMPLETED,
  [LeadStatus.Enum.PROSPECT]: GetExternalLeadResponseStatus.PROSPECT,
  [LeadStatus.Enum.SUBMITTED]: GetExternalLeadResponseStatus.SUBMITTED,
  [LeadStatus.Enum.ENRICHED]: GetExternalLeadResponseStatus.ENRICHED,
  [LeadStatus.Enum.SCORED]: GetExternalLeadResponseStatus.SCORED,
  [LeadStatus.Enum.DELIVERED]: GetExternalLeadResponseStatus.DELIVERED,
  [LeadStatus.Enum.ROUTED]: GetExternalLeadResponseStatus.ROUTED,
};

const qualifyingLifeEventAnswersMap = {
  [QualifyingLifeEventDto.Enum['lost-coverage']]:
    QualifyingLifeEvent.Enum.LostCoverage,
  [QualifyingLifeEventDto.Enum['got-new-job']]:
    QualifyingLifeEvent.Enum.IncomeChanged,
  [QualifyingLifeEventDto.Enum['had-a-baby']]: QualifyingLifeEvent.Enum.Baby,
  [QualifyingLifeEventDto.Enum['moved-new-state']]:
    QualifyingLifeEvent.Enum.Moved,
  [QualifyingLifeEventDto.Enum['none-of-these']]:
    QualifyingLifeEvent.Enum.Other,
};

export function isConsideredSoldBasedOnStatus(
  status: Nullable<GetExternalLeadResponseStatus>,
) {
  return (
    status === GetExternalLeadResponseStatus.SUBMITTED ||
    status === GetExternalLeadResponseStatus.ENRICHED ||
    status === GetExternalLeadResponseStatus.SCORED ||
    status === GetExternalLeadResponseStatus.ROUTED ||
    status === GetExternalLeadResponseStatus.DELIVERED
  );
}

export function getProspectAdapterFactory({
  baseUrl,
}: {
  baseUrl: string;
}): GetProspectRepository {
  return {
    getProspect: async (params) => {
      if (!params.sessionId && !params.userId) {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToGetProspect',
            },
          ],
        });
      }

      const leadStatusId = params.leadStatus
        ? leadStatusMap[params.leadStatus].toString()
        : undefined;

      const response = await getExternalLeadService({ baseUrl })({
        ...params,
        leadStatusId,
      });

      if (response.status !== 200 || response.body === undefined) {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToGetProspect',
              rootCause: new Error(
                `Session ${params.sessionId} could not be retreived`,
              ),
            },
          ],
        });
      }

      const { userAttributes, journeyAttributes } = response.body;

      const userAttributesMap = userAttributesMapper(userAttributes);

      const journeyAttributesMap = journeyAttributesMapper(journeyAttributes);

      const { applicant, spouse, children } = memberMapper(
        userAttributes,
        userAttributesMap,
      );

      let qualifyingLifeEvent;
      if (journeyAttributesMap['quality-life-event']) {
        qualifyingLifeEvent =
          qualifyingLifeEventAnswersMap[
            journeyAttributesMap['quality-life-event']
          ];
      }

      const leadSession: Prospect = {
        landingUrl: response.body?.landingUrl || undefined,
        location: locationMapper(userAttributesMap),
        householdSize: userAttributesMap.householdSize || 0,
        householdIncome: userAttributesMap.householdIncome as HouseholdIncome,
        federalPovertyLevel: userAttributesMap.fpl as FederalPovertyLevel,
        qualifyingLifeEvent,
        applicant,
        spouse,
        children,
        contactInformation: contactMapper(userAttributesMap),
        leadId: response.body.tcpaUniversalId
          ? response.body.tcpaUniversalId
          : undefined,
        sold: isConsideredSoldBasedOnStatus(response.body.leadStatusId),
      };

      return createSuccessOperationResult(leadSession);
    },
  };
}
